import castArray from "lodash/fp/castArray";
import filter from "lodash/fp/filter";
import find from "lodash/fp/find";
import flow from "lodash/fp/flow";
import get from "lodash/fp/get";
import getOr from "lodash/fp/getOr";
import head from "lodash/fp/head";
import has from "lodash/fp/has";
import isArray from "lodash/fp/isArray";
import keys from "lodash/fp/keys";
import keyBy from "lodash/fp/keyBy";
import map from "lodash/fp/map";
import mapValues from "lodash/fp/mapValues";
import pick from "lodash/fp/pick";
import pickBy from "lodash/fp/pickBy";
import reduce from "lodash/fp/reduce";
import replace from "lodash/fp/replace";
import sortBy from "lodash/fp/sortBy";
import uniqBy from "lodash/fp/uniqBy";
import values from "lodash/fp/values";

import selectUserChoices from "./selectUserChoices";
import { getFilteredOptions, getFlatFields, shouldBeDisplayed } from "utils";

const invalidImageURLS = [
  "https://cdn.media.amplience.net/i/bauer/SUPREME_SUPREME_BLOCKER_VELCROELASTIC",
  "https://cdn.media.amplience.net/i/bauer/SUPREME_SUPREME_GLOVETTRAP_VELCROELASTIC",
  "https://cdn.media.amplience.net/i/bauer/SUPREME_SUPREME_GLOVE_VELCROELASTIC",
  "https://cdn.media.amplience.net/i/bauer/SUPREME_VAPOR_BLOCKER_VELCROELASTIC",
];

// Util fns
const removeAnyWithNoUrlOrComponent = flow(
  map(
    flow(
      filter((a) => Boolean(a?.url) || Boolean(a?.component)),
      head
    )
  ),
  filter(Boolean)
);

const reduceWithIndex = reduce.convert({ cap: false });

const DEFAULT = "DEFAULT";

const isNotDefault = (str) => str !== DEFAULT;

const applyDefaultImages = ({
  allFields,
  imageAngle,
  imageSet,
  userChoiceValues,
}) =>
  flow(
    map((arr) => {
      const [value, ...fieldNames] = [...arr].reverse();

      if (has("component")(value)) {
        return [value];
      }

      return fieldNames
        .map((name, index) => {
          if (typeof name === "string") {
            return { name, value: index === 0 ? value : null };
          }
          return name;
        })
        .reverse();
    }),

    map(
      map(({ name, value, ...rest }) => {
        const defaultImage = flow(
          getOr([], "options"),
          (options) => getFilteredOptions({ options, userChoiceValues }),
          find({ value }),
          get(["images", imageAngle, imageSet].filter(isNotDefault))
        )(allFields[name]);

        return {
          name,
          ...defaultImage,
          ...rest,
        };
      })
    )
  );

const makeImagesObject = reduceWithIndex(
  (a, c, i) => ({
    ...a,
    [c.name]: {
      ...c,
      order: i,
    },
  }),
  {}
);

export function makeUrl({
  url,
  allFields,
  userChoices,
  fallbackImages = {},
  imageURLValueToUse = "imageURLValue",
}) {
  return url
    .split(/(\[[A-Za-z0-9_-]*\])/g)
    .map((str) => {
      if (str.startsWith("[") && str.endsWith("]")) {
        const fieldName = str.replace(/\[|\]/g, "");

        const userSelectedOption = getOr({}, [fieldName, "selectedOption"])(
          userChoices
        );

        const fallbackSelectedOption =
          flow(
            getOr([], [fieldName, "options"]),
            filter(
              ({ value }) =>
                !has(fieldName)(fallbackImages) ||
                value === fallbackImages[fieldName]
            ),
            head
          )(allFields) || {};

        return (
          userSelectedOption?.[imageURLValueToUse] ||
          fallbackSelectedOption?.[imageURLValueToUse]
        );
      }
      return str;
    })
    .join("");
}

function makeUrlWithProductFallbackImages(
  { url },
  userChoices,
  productImages = [],
  allFields
) {
  const productImagesFallbackValues = productImages
    .map((arrOrObj) => {
      if (isArray(arrOrObj)) {
        const [name, value] = arrOrObj.slice(-2);
        return { name, value };
      }
      return arrOrObj;
    })
    .reduce((acc, { name, value }) => ({ ...acc, [name]: value }), {});

  return makeUrl({
    url,
    allFields,
    userChoices,
    fallbackImages: productImagesFallbackValues,
  });
}

const replaceImagesWithUserChoiceImages =
  ({ userChoiceImages, imageSet }) =>
  (something) => {
    const getWithImageSet = (a, b) =>
      get([a, imageSet, b].filter(isNotDefault));

    return map((field) => {
      if (has("component")(field)) {
        return field;
      }

      return {
        ...field,
        url: getWithImageSet(field.name, "url")(userChoiceImages) || field.url,
        alt: getWithImageSet(field.name, "alt")(userChoiceImages) || field.alt,
      };
    })(something);
  };

// Allows callouts to appear on certain customisations
const shouldBeComponentToDisplay =
  (selectedCustomisationId) =>
  ({ displayOnCustomisationIds = [] } = {}) => {
    return (
      displayOnCustomisationIds.length === 0 ||
      displayOnCustomisationIds.includes(selectedCustomisationId)
    );
  };

const removeAnyThatShouldBeHidden = (selectedCustomisationId) =>
  filter(shouldBeComponentToDisplay(selectedCustomisationId));

const filterInvalidImageURLs = filter(({ url }) =>
  invalidImageURLS.every((invalidImageURL) => !url?.startsWith(invalidImageURL))
);

// Selector fn
// a lot is happening here:
// 1) get base image(s) for the product,
// 2) apply any images based on the users customisations
// 3) an "image" could have a url, or a component attribute
// additionally `displayOnCustomisationIds` array can be specified to limit when the image is displayed - if omitted or empty, the image will be displayed
export default function selectProductImagesForAngle(imageAngle) {
  return (state) => {
    const {
      selectedProductId,
      selectedCustomisationId,
      formData,
      userChoiceValues,
    } = state.app;

    const userChoices = selectUserChoices(state);

    const imageSets = flow(
      keys,
      filter((key) => key.startsWith("customise-") && userChoiceValues[key]),
      map(replace("customise-", ""))
    )(userChoiceValues);

    const selectedProduct = flow(
      getOr([], ["products"]),
      find({ id: selectedProductId })
    )(formData);

    const allFields = flow(
      getOr([], "customisations"),
      getFlatFields,
      uniqBy("name"),
      filter(shouldBeDisplayed(userChoiceValues)),
      keyBy(get("name"))
    )(selectedProduct);

    const userChoiceImages = flow(
      mapValues(get(["selectedOption", "images", imageAngle])),
      pickBy(shouldBeComponentToDisplay(selectedCustomisationId))
    )(userChoices);

    const imageSetOrder = getOr(
      [],
      ["images", "imageSetOrder"]
    )(selectedProduct);

    return flow(
      getOr([], ["images", imageAngle, "images"]),
      (objOrArray) => {
        if (isArray(objOrArray)) {
          return { [DEFAULT]: objOrArray };
        }
        return pick(imageSets)(objOrArray);
      },
      reduceWithIndex((acc, images, imageSet) => {
        return [
          ...acc,
          flow(
            castArray,

            // convert object to array of where to get value from
            map(
              flow((objOrArray) => {
                return has("name")(objOrArray) && has("value")(objOrArray)
                  ? [objOrArray.name, objOrArray.value]
                  : objOrArray;
              }, castArray)
            ),

            applyDefaultImages({
              allFields,
              imageAngle,
              imageSet,
              userChoiceValues,
            }),

            map(
              replaceImagesWithUserChoiceImages({
                userChoiceImages,
                imageSet,
              })
            ),

            removeAnyWithNoUrlOrComponent,

            removeAnyThatShouldBeHidden(selectedCustomisationId),
            // Filters based on userChoiceValues & the Field's displayIf condition (e.g for Goalie Gear, if user selects graphic: "supreme mach", should remove "outside-zone-color")
            filter(({ name }) =>
              shouldBeDisplayed(userChoiceValues)(allFields[name])
            ),

            // Filters out any callOuts that have displayIfs
            filter(shouldBeDisplayed(userChoiceValues)),

            makeImagesObject,
            values,
            sortBy("order"),
            map((imageObj) => {
              if (
                imageObj?.url?.includes("[") &&
                imageObj?.url?.includes("]")
              ) {
                return {
                  ...imageObj,
                  url: makeUrlWithProductFallbackImages(
                    imageObj,
                    userChoices,
                    images,
                    allFields
                  ),
                };
              }
              return imageObj;
            }),
            filterInvalidImageURLs,

            (imagesArray) => ({ imageSet, images: imagesArray })
          )(images),
        ];
      }, []),
      sortBy(({ imageSet }) => imageSetOrder.indexOf(imageSet))
    )(selectedProduct);
  };
}
